import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TrendingDown } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { MobileListCard, MobileListCardStack } from '@/components/ui/MobileListCard';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  EditableCurrency,
  EditableNumber,
  EditableSelect,
} from '@/components/inventory/EditableItemField';
import { EditableKV, KV, SummaryCard } from '@/components/equipment/DetailRows';
import {
  equipmentSchedule,
  useUpdateEquipment,
  type Equipment,
  type EquipmentPatch,
} from '@/features/equipment/hooks';
import {
  depreciationAsOf,
  formatMonthKey,
  type DepreciationSchedule,
} from '@/features/equipment/depreciation';
import {
  DEPRECIATION_METHOD_META,
  DEPRECIATION_METHOD_OPTIONS,
  EQUIPMENT_CATEGORY_META,
  MACRS_CLASSES,
  isDepreciationMethod,
  isEquipmentCategory,
  type DepreciationMethod,
} from '@/features/equipment/model';
import { BRAND } from '@/lib/brand';
import { formatCurrency, formatDateOnly, todayIsoDate } from '@/lib/formatters';
import { useIsMobile } from '@/lib/useMediaQuery';
import { cn } from '@/lib/utils';

const MACRS_OPTIONS = MACRS_CLASSES.map((years) => ({
  value: String(years),
  label: `${years}-year property`,
  hint:
    years === 5
      ? 'Computers, office machines, light vehicles'
      : years === 7
        ? 'Office furniture, most manufacturing equipment'
        : years === 3
          ? 'Some tools and special handling devices'
          : undefined,
}));

function nearestMacrsClass(life: number | null, fallback: number): number {
  const target = life ?? fallback;
  return MACRS_CLASSES.reduce((best, c) => (Math.abs(c - target) < Math.abs(best - target) ? c : best), 7);
}

export function EquipmentDepreciationTab({
  equipment: e,
  canEdit,
  onOpenTab,
}: {
  equipment: Equipment;
  canEdit: boolean;
  onOpenTab: (tab: string) => void;
}) {
  const update = useUpdateEquipment();
  const schedule = useMemo(() => equipmentSchedule(e), [e]);
  const today = todayIsoDate();
  const position = useMemo(() => depreciationAsOf(schedule, today), [schedule, today]);
  const method: DepreciationMethod = isDepreciationMethod(e.depreciation_method)
    ? e.depreciation_method
    : 'straight_line';
  const usesLife = method !== 'expensed' && method !== 'none';
  const usesSalvage = method === 'straight_line' || method === 'declining_balance' || method === 'sum_of_years_digits';

  const save = async (patch: EquipmentPatch) => {
    await update.mutateAsync({ id: e.id, patch });
  };

  const onMethodChange = async (next: DepreciationMethod) => {
    const patch: EquipmentPatch = { depreciation_method: next };
    if (next === 'macrs' && !(MACRS_CLASSES as readonly number[]).includes(e.useful_life_years ?? -1)) {
      const fallback = isEquipmentCategory(e.category)
        ? EQUIPMENT_CATEGORY_META[e.category].defaultLifeYears
        : 7;
      patch.useful_life_years = nearestMacrsClass(e.useful_life_years, fallback);
    }
    await save(patch);
  };

  const thisYear = Number(today.slice(0, 4));
  const thisYearRow = schedule.kind === 'schedule' ? schedule.rows.find((r) => r.year === thisYear) : undefined;
  const startOn = e.in_service_on ?? e.acquired_at;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard label="Cost" value={formatCurrency(e.purchase_price_cents, { whole: true })} />
        <SummaryCard
          label="Accumulated to date"
          value={position ? formatCurrency(position.accumulatedCents, { whole: true }) : '—'}
        />
        <SummaryCard
          label="Book value today"
          value={position ? formatCurrency(position.bookValueCents, { whole: true }) : '—'}
          caption={position?.disposed ? 'Disposed' : undefined}
        />
        <SummaryCard
          label={`${thisYear} expense`}
          value={
            schedule.kind === 'schedule'
              ? formatCurrency(thisYearRow?.depreciationCents ?? 0, { whole: true })
              : '—'
          }
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Method</CardTitle>
            <CardDescription>{DEPRECIATION_METHOD_META[method].hint}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            <EditableKV
              label="Method"
              editor={
                <EditableSelect
                  value={method}
                  options={DEPRECIATION_METHOD_OPTIONS}
                  ariaLabel="Depreciation method"
                  canEdit={canEdit}
                  density="kv"
                  align="right"
                  onSave={onMethodChange}
                />
              }
            />
            {usesLife && method === 'macrs' && (
              <EditableKV
                label="Recovery period"
                editor={
                  <EditableSelect
                    value={String(e.useful_life_years ?? 7)}
                    options={MACRS_OPTIONS}
                    ariaLabel="Recovery period"
                    canEdit={canEdit}
                    density="kv"
                    align="right"
                    display={<span>{e.useful_life_years ?? '—'} years</span>}
                    onSave={(next) => save({ useful_life_years: Number(next) })}
                  />
                }
              />
            )}
            {usesLife && method !== 'macrs' && (
              <EditableKV
                label="Useful life"
                editor={
                  <EditableNumber
                    value={e.useful_life_years}
                    min={1}
                    ariaLabel="Useful life in years"
                    canEdit={canEdit}
                    density="kv"
                    align="right"
                    display={
                      e.useful_life_years == null ? (
                        <span className="text-muted-foreground">Not set</span>
                      ) : (
                        <span>
                          {e.useful_life_years} year{e.useful_life_years === 1 ? '' : 's'}
                        </span>
                      )
                    }
                    onSave={async (next) => {
                      if (next != null && next > 100) throw new Error('Useful life is capped at 100 years.');
                      await save({ useful_life_years: next });
                    }}
                  />
                }
              />
            )}
            {usesSalvage && (
              <EditableKV
                label="Salvage value"
                editor={
                  <EditableCurrency
                    value={e.salvage_value_cents}
                    ariaLabel="Salvage value"
                    canEdit={canEdit}
                    density="kv"
                    align="right"
                    onSave={(next) => save({ salvage_value_cents: next })}
                  />
                }
              />
            )}
            <KV
              label="Starts"
              value={
                <button
                  type="button"
                  className="min-h-9 text-atmosphere-dark hover:underline"
                  onClick={() => onOpenTab('purchase')}
                  title={
                    e.in_service_on
                      ? 'In-service date — change on Purchase & warranty'
                      : 'Purchase date — set an in-service date on Purchase & warranty'
                  }
                >
                  {startOn ? formatDateOnly(startOn) : 'Set a date'}
                </button>
              }
            />
            {e.disposed_on && <KV label="Disposed" value={formatDateOnly(e.disposed_on)} />}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Book value by year</CardTitle>
          </CardHeader>
          <CardContent>
            <ScheduleChart schedule={schedule} onOpenTab={onOpenTab} />
          </CardContent>
        </Card>
      </div>

      {schedule.kind === 'schedule' && (
        <Card>
          <CardHeader>
            <CardTitle>Schedule</CardTitle>
            <CardDescription>
              {method === 'macrs'
                ? 'IRS GDS half-year convention, one row per tax year. Confirm with your accountant before filing.'
                : `Monthly from ${formatMonthKey(schedule.start)}, rolled up by calendar year.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScheduleTable schedule={schedule} currentYear={thisYear} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ScheduleChart({
  schedule,
  onOpenTab,
}: {
  schedule: DepreciationSchedule;
  onOpenTab: (tab: string) => void;
}) {
  if (schedule.kind === 'incomplete') {
    return (
      <EmptyState
        icon={TrendingDown}
        title={`Add the ${joinWords(schedule.missing)} to build the schedule`}
        className="py-10"
        action={
          schedule.missing.some((m) => m.includes('price') || m.includes('date')) ? (
            <Button variant="outline" onClick={() => onOpenTab('purchase')}>
              Go to Purchase & warranty
            </Button>
          ) : undefined
        }
      />
    );
  }
  if (schedule.kind === 'not_depreciated') {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Not depreciated — carried at its cost of {formatCurrency(schedule.costCents)}.
      </p>
    );
  }
  if (schedule.rows.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        No depreciation was taken before disposal.
      </p>
    );
  }

  const data = schedule.rows.map((r) => ({
    year: String(r.year),
    book: r.endingCents / 100,
    expense: r.depreciationCents / 100,
  }));
  const money = (v: number) => formatCurrency(Math.round(v * 100), { compact: true });

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 4, right: 16, left: 0 }}>
          <CartesianGrid stroke={BRAND.linenDark} vertical={false} />
          <XAxis dataKey="year" stroke={BRAND.oldGrowth} fontSize={11} minTickGap={8} />
          <YAxis stroke={BRAND.oldGrowth} fontSize={11} width={56} tickFormatter={money} />
          <Tooltip
            formatter={(value, key) => [
              formatCurrency(Math.round(Number(value) * 100)),
              key === 'book' ? 'Book value (year end)' : 'Depreciation',
            ]}
            contentStyle={{
              background: '#fff',
              border: `1px solid ${BRAND.linenDark}`,
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Bar dataKey="expense" fill={BRAND.brightMoss} radius={[3, 3, 0, 0]} maxBarSize={28} />
          <Line
            type="monotone"
            dataKey="book"
            stroke={BRAND.humus}
            strokeWidth={2}
            dot={{ r: 2.5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function ScheduleTable({
  schedule,
  currentYear,
}: {
  schedule: Extract<DepreciationSchedule, { kind: 'schedule' }>;
  currentYear: number;
}) {
  const isMobile = useIsMobile();
  const total = schedule.rows.reduce((acc, r) => acc + r.depreciationCents, 0);

  if (schedule.rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No depreciation was taken before disposal.</p>;
  }

  if (isMobile) {
    return (
      <MobileListCardStack>
        {schedule.rows.map((r) => (
          <MobileListCard
            key={r.year}
            title={String(r.year)}
            subtitle={`${r.months} month${r.months === 1 ? '' : 's'}`}
            badges={r.year === currentYear ? <Badge variant="info-soft">This year</Badge> : undefined}
            amount={{ value: formatCurrency(r.depreciationCents), caption: 'Depreciation' }}
            meta={[
              { label: 'Start', value: formatCurrency(r.beginningCents) },
              { label: 'End', value: formatCurrency(r.endingCents) },
              { label: 'Accumulated', value: formatCurrency(r.accumulatedCents) },
            ]}
            trailing={null}
          />
        ))}
      </MobileListCardStack>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Year</TableHead>
          <TableHead className="text-right">Months</TableHead>
          <TableHead className="text-right">Beginning book value</TableHead>
          <TableHead className="text-right">Depreciation</TableHead>
          <TableHead className="text-right">Accumulated</TableHead>
          <TableHead className="text-right">Ending book value</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {schedule.rows.map((r) => (
          <TableRow key={r.year} className={cn(r.year === currentYear && 'bg-info-soft/15')}>
            <TableCell className="font-medium">
              <span className="inline-flex items-center gap-2">
                {r.year}
                {r.year === currentYear && <Badge variant="info-soft">This year</Badge>}
              </span>
            </TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">{r.months}</TableCell>
            <TableCell className="text-right tabular-nums">{formatCurrency(r.beginningCents)}</TableCell>
            <TableCell className="text-right tabular-nums font-medium">
              {formatCurrency(r.depreciationCents)}
            </TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">
              {formatCurrency(r.accumulatedCents)}
            </TableCell>
            <TableCell className="text-right tabular-nums">{formatCurrency(r.endingCents)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell colSpan={3}>Total</TableCell>
          <TableCell className="text-right tabular-nums">{formatCurrency(total)}</TableCell>
          <TableCell colSpan={2} className="text-right text-xs font-normal text-muted-foreground">
            {schedule.salvageCents > 0
              ? `Salvage value ${formatCurrency(schedule.salvageCents)}`
              : schedule.disposedOn
                ? `Disposed ${formatDateOnly(schedule.disposedOn)}`
                : ''}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

function joinWords(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
